// deps
import React, { memo } from "react";

// types
import { ISavingCards } from "@dineout-payment-success-ui/interfaces";

// styles
import {
    SavingsCardWrapper,
    CardsWrapper,
    CardBody,
    CartDetailsItem,
    AmountWrapper,
    TextWrapper,
    CartItems,
    VerticalDivider,
    Nudge,
    AmountImageWrapper,
    TextItem,
    NudgeNoSavings,
    SavingsStack,
    SavingsCardRoot,
    SavingsHeaderStrip,
    SavingsHeaderTitle,
    SavingsHeaderBanner,
    SavingsBodyRow,
    CoinImageWrap,
    AmountDisplay,
    SavingsVerticalRule,
    DescriptionWrap,
    ScenesNudge,
} from "./style";

// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// Constants
import { AMOUNT_FORMAT, ONE_LOGO_TEXT } from "@dineout-payment-success-ui/constants";

// internal deps
import { format, isEmpty, replaceText } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

type SavingCardEntry = ISavingCards["cards"][number];

const getAmountColorOverride = (card: SavingCardEntry): string | undefined =>
    !isEmpty(card.amountColor) ? (card.amountColor as string) : undefined;

const SavingsCardItem: React.FC<{ title: string; card: SavingCardEntry }> = ({ title, card }) => {
    const amountColorOverride = getAmountColorOverride(card) as keyof IColors;
    const showCoin = Boolean(card.amountImage);
    const amountLabel = showCoin ? card.amount : format(AMOUNT_FORMAT, card.amount);

    return (
        <SavingsCardRoot $backgroundImage={card.backgroundImage}>
            <SavingsHeaderStrip $isFullBanner={Boolean(card.headerImage)}>
                {card.headerImage ? (
                    <SavingsHeaderBanner>
                        <Image src={card.headerImage} alt={title} height="40px" width="100%" />
                    </SavingsHeaderBanner>
                ) : (
                    <SavingsHeaderTitle variant="V2.overline.neutral.bold" color="text_Highest_Emphasis">
                        {title}
                    </SavingsHeaderTitle>
                )}
            </SavingsHeaderStrip>
            <SavingsBodyRow>
                {showCoin ? (
                    <CoinImageWrap>
                        <Image src={card.amountImage} alt="" height="28px" width="28px" />
                    </CoinImageWrap>
                ) : null}
                <AmountDisplay color={amountColorOverride || "positive"} variant="V2.heading1.neutral.black">
                    {amountLabel}
                </AmountDisplay>
                <SavingsVerticalRule />
                <DescriptionWrap>
                    <Text variant="V2.overline.neutral.medium" color="text_High_Emphasis">
                        {replaceText(card.text, ONE_LOGO_TEXT, true)}
                    </Text>
                    {card.nudge?.text ? (
                        <ScenesNudge>
                            <Text variant="V2.caption.neutral.bold" color="warning">
                                {card?.nudge?.text}
                            </Text>
                        </ScenesNudge>
                    ) : null}
                </DescriptionWrap>
            </SavingsBodyRow>
        </SavingsCardRoot>
    );
};

const SavingsCard: React.FC<ISavingCards> = ({ title, cards, isScenesFlow }) => {
    if (isScenesFlow) {
        return (
            <SavingsStack>
                {cards.map((card, index) => (
                    <SavingsCardItem key={index} title={title} card={card} />
                ))}
            </SavingsStack>
        );
    }

    const cardSize = cards.length;

    if (cardSize === 1) {
        return (
            <SavingsCardWrapper>
                <Text variant="V2.body1.neutral.bold" color="text_Highest_Emphasis">
                    {title}
                </Text>
                <CardsWrapper>
                    <CardBody $backgroundImage={cards[0].backgroundImage}>
                        <CartItems>
                            <AmountWrapper variant="V2.heading1.neutral.black" color={cards[0].textColor}>
                                <AmountImageWrapper>
                                    <Image src={cards[0].amountImage} alt="amount" height="24px" width="24px" />
                                    {cards[0].amount}
                                </AmountImageWrapper>
                            </AmountWrapper>
                            <VerticalDivider />
                            <TextItem>
                                <TextWrapper variant="V2.overline.neutral.medium" color="text_Low_Emphasis">
                                    {replaceText(cards[0].text)}
                                </TextWrapper>
                                <NudgeNoSavings>
                                    <Text variant="V2.caption.neutral.bold" color="warning">
                                        {cards[0].nudge?.text}
                                    </Text>
                                </NudgeNoSavings>
                            </TextItem>
                        </CartItems>
                    </CardBody>
                </CardsWrapper>
            </SavingsCardWrapper>
        );
    }

    // For multiple cards
    return (
        <SavingsCardWrapper>
            <Text variant="V2.body1.neutral.bold" color="text_Highest_Emphasis">
                {title}
            </Text>
            <CardsWrapper>
                {cards.map((card, index) => (
                    <CardBody key={index} isMultipleCard={true} $backgroundImage={card.backgroundImage}>
                        <CartDetailsItem>
                            <AmountWrapper variant="V2.heading1.neutral.black" color={card.textColor}>
                                {card.amountImage ? (
                                    <AmountImageWrapper>
                                        <Image src={card.amountImage} alt="amount" height="28px" width="28px" />
                                        {card.amount}
                                    </AmountImageWrapper>
                                ) : (
                                    format(AMOUNT_FORMAT, card.amount)
                                )}
                            </AmountWrapper>
                            <TextWrapper variant="V2.overline.neutral.medium" color="text_Low_Emphasis">
                                {replaceText(card.text, ONE_LOGO_TEXT, true)}
                            </TextWrapper>
                            {card.nudge?.text && (
                                <Nudge>
                                    <Text variant="V2.caption.neutral.bold" color="warning">
                                        {card.nudge?.text}
                                    </Text>
                                </Nudge>
                            )}
                        </CartDetailsItem>
                    </CardBody>
                ))}
            </CardsWrapper>
        </SavingsCardWrapper>
    );
};

export default memo(SavingsCard);
